import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Users, TrendingUp, AlertTriangle } from 'lucide-react';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { statusFromChartRows } from '../../shared/components/dashboard/moduleDashboardCharts';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const VALIDATION_ACTIVE = ['validated', 'approved', 'complete', 'processed', 'paid'];

export default function SalairesAnalysesRapportsPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salairesAnalysesRapportsPage.${key}`, opts);

  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState({});
  const [departments, setDepartments] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, deptRes] = await Promise.all([
        localApiAdapter.get('/salaries/dashboard/stats').catch(() => null),
        localApiAdapter.get('/salaries/dashboard/department-breakdown').catch(() => []),
      ]);
      setStatsData(statsRes || {});
      setDepartments(Array.isArray(deptRes) ? deptRes : []);
    } catch (error) {
      console.error('[SalairesAnalyses] load error:', error);
      setStatsData({});
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const gosiEmployee = num(statsData.gosiEmployee ?? statsData.cnas);
  const gosiEmployer =
    num(statsData.gosiEmployer) || Math.max(num(statsData.totalCnasDas) - gosiEmployee, 0);
  const gosiTotal = gosiEmployee + gosiEmployer;
  const totalEmployees = num(statsData.totalEmployees);
  const totalPayroll = num(statsData.totalPayroll);
  const pendingValidations = num(statsData.pendingValidations);
  const payrollPerEmployee =
    num(statsData.payrollPerEmployee) ||
    (totalEmployees > 0 ? Math.round(totalPayroll / totalEmployees) : num(statsData.avgSalary));

  const stats = useMemo(
    () => [
      {
        label: p('stat_total_payroll'),
        value: formatBrandMoney(totalPayroll),
        icon: DollarSign,
      },
      {
        label: p('stat_total_employees'),
        value: totalEmployees,
        icon: Users,
      },
      {
        label: p('stat_avg_salary'),
        value: formatBrandMoney(num(statsData.avgSalary)),
        icon: TrendingUp,
      },
      {
        label: p('stat_pending_validations'),
        value: pendingValidations,
        icon: AlertTriangle,
      },
    ],
    [statsData, totalPayroll, totalEmployees, pendingValidations, p]
  );

  const insights = useMemo(
    () => [
      {
        label: p('insight_gosi_total'),
        value: formatBrandMoney(gosiTotal),
        hint: p('insight_gosi_total_hint', {
          employee: formatBrandMoney(gosiEmployee),
          employer: formatBrandMoney(gosiEmployer),
        }),
      },
      {
        label: p('insight_pending_validations'),
        value: String(pendingValidations),
        hint: p('insight_pending_validations_hint'),
      },
      {
        label: p('insight_payroll_per_employee'),
        value: formatBrandMoney(payrollPerEmployee),
        hint: p('insight_payroll_per_employee_hint', { count: totalEmployees }),
      },
    ],
    [gosiTotal, gosiEmployee, gosiEmployer, pendingValidations, payrollPerEmployee, totalEmployees, p]
  );

  const charts = useMemo(() => {
    const deptRows = [...departments]
      .sort(
        (a, b) =>
          num(b.totalPayroll ?? b.total ?? b.amount) - num(a.totalPayroll ?? a.total ?? a.amount)
      )
      .slice(0, 8)
      .map((row) => ({
        label: row.department || row.name || p('na'),
        value: num(row.totalPayroll ?? row.total ?? row.amount),
        display: formatBrandMoney(num(row.totalPayroll ?? row.total ?? row.amount)),
      }));

    const validationRows = statsData.validationByStatus || statsData.statusBreakdown || [];
    const { active, inactive } = statusFromChartRows(validationRows, VALIDATION_ACTIVE);
    const validatedCount = num(statsData.validatedCount ?? statsData.validatedPayslips);

    const totalBase =
      num(statsData.totalBaseSalary ?? statsData.basePayroll) ||
      Math.max(totalPayroll - num(statsData.totalBonuses), 0);
    const totalBonuses = num(statsData.totalBonuses);

    return [
      {
        title: p('chart_by_department'),
        subtitle: p('chart_by_department_subtitle'),
        variant: 'ranked',
        rows: deptRows,
      },
      {
        title: p('chart_validation_status'),
        subtitle: p('chart_validation_status_subtitle'),
        variant: 'status',
        active: active || validatedCount || Math.max(0, totalEmployees - pendingValidations),
        inactive: inactive || pendingValidations,
        alwaysShow: true,
      },
      {
        title: p('chart_bonuses_vs_base'),
        subtitle: p('chart_bonuses_vs_base_subtitle'),
        variant: 'pipeline',
        alwaysShow: true,
        rows: [
          { label: p('chart_base_salary'), value: totalBase, display: formatBrandMoney(totalBase) },
          {
            label: p('stat_total_bonuses'),
            value: totalBonuses,
            display: formatBrandMoney(totalBonuses),
          },
        ],
      },
    ];
  }, [departments, statsData, totalPayroll, totalEmployees, pendingValidations, p]);

  const table = useMemo(() => {
    if (departments.length) {
      return {
        title: p('table_by_department'),
        columns: [
          { key: 'department', label: p('col_department') },
          { key: 'employees', label: p('col_employees') },
          { key: 'total', label: p('col_total') },
          { key: 'avg', label: p('col_avg') },
        ],
        rows: departments.map((row) => ({
          department: row.department || row.name || p('na'),
          employees: num(row.employeeCount ?? row.count ?? row.employees),
          total: formatBrandMoney(num(row.totalPayroll ?? row.total ?? row.amount)),
          avg: formatBrandMoney(num(row.avgSalary ?? row.average)),
        })),
      };
    }
    return {
      title: p('table_summary'),
      columns: [
        { key: 'metric', label: p('col_metric') },
        { key: 'value', label: p('col_value') },
      ],
      rows: [
        { metric: p('stat_total_payroll'), value: formatBrandMoney(totalPayroll) },
        { metric: p('stat_total_employees'), value: totalEmployees },
        { metric: p('stat_avg_salary'), value: formatBrandMoney(num(statsData.avgSalary)) },
        { metric: p('stat_total_bonuses'), value: formatBrandMoney(num(statsData.totalBonuses)) },
        { metric: p('stat_gosi_employee'), value: formatBrandMoney(gosiEmployee) },
        { metric: p('stat_gosi_employer'), value: formatBrandMoney(gosiEmployer) },
        { metric: p('stat_pending_validations'), value: pendingValidations },
        { metric: p('insight_payroll_per_employee'), value: formatBrandMoney(payrollPerEmployee) },
      ],
    };
  }, [departments, statsData, totalPayroll, totalEmployees, pendingValidations, payrollPerEmployee, gosiEmployee, gosiEmployer, p]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={loadData}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="salaires_analyses"
      emptyLabel={p('empty')}
    />
  );
}
